import React from "react";
import { Check, Circle } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const pricingTiers = [{
  name: "Clarity Session",
  description: "Perfect for pinpointing your immediate creative bottlenecks and blocks.",
  price: "$0",
  features: ["30-Minute Strategy Call", "Immediate Action Plan", "Resource Recommendations", "Creative Audit Prep", "Post-Session Summary"],
  isFeatured: false
}, {
  name: "Signature Growth",
  description: "Our core program for scaling your creative impact and business systems.",
  price: "$2,497",
  features: ["Full Ecosystem Audit", "8 Weekly Strategy Calls", "Custom Scaling Roadmap", "Brand Voice Alignment", "Implementation Support", "Community Network Access"],
  isFeatured: false
}, {
  name: "VIP Elite",
  description: "Your long-term visibility partner. For founders who want comprehensive, ongoing support to build and sustain their authority.",
  price: "$9,997",
  features: ["1:1 Monthly Immersion", "Unlimited Voxer Support", "Team Training Session", "Proprietary Growth Tools", "Done-For-You Assets", "Priority Booking Access"],
  isFeatured: true
}];
export default function Pricing() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="27169" id="select-your-level" ref={ref} className="bg-background py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className={cn("text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-4xl md:text-5xl font-serif font-medium text-foreground mb-4">
            Select Your Level
          </h2>
          <p className="text-muted-foreground text-lg font-sans">
            Strategic investment tiers designed to guide you from initial clarity 
            to sustainable market leadership.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center max-w-6xl mx-auto">
          {pricingTiers.map((tier, i) => <div key={tier.name} data-index={i} className={cn("transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")} style={{
          transitionDelay: `${i * 150}ms`
        }}>
              <Card className={cn("border-border bg-card/80 backdrop-blur-md relative flex flex-col h-full transition-transform duration-300 hover:shadow-lg", tier.isFeatured ? "md:scale-105 shadow-xl z-10 border-accent border-[1.5px]" : "shadow-sm hover:-translate-y-1 hover:border-secondary/30")}>
                {tier.isFeatured && <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-accent text-accent-foreground px-4 py-1 rounded-full text-xs font-sans font-medium tracking-wider uppercase shadow-sm">
                    Most Transformative
                  </div>}

                <CardHeader className="text-center pt-10 pb-6 border-b border-border/20">
                  <CardTitle className="font-serif text-2xl md:text-3xl font-normal text-foreground">
                    {tier.name}
                  </CardTitle>
                  <p className="text-muted-foreground text-sm mt-2 font-sans px-4">
                    {tier.description}
                  </p>
                </CardHeader>

                <CardContent className="flex-grow py-8 px-6 md:px-8">
                  <ul className="space-y-4">
                    {tier.features.map((feature, idx) => <li key={idx} data-index={idx} className="flex items-start gap-3 text-sm text-muted-foreground">
                        <div className="mt-1.5 shrink-0">
                          <Circle className="size-2 fill-primary text-primary" />
                        </div>
                        <span className="leading-relaxed font-sans">{feature}</span>
                      </li>)}
                  </ul>
                </CardContent>

                <CardFooter className="flex flex-col items-center pb-10 pt-2 gap-6">
                  <div className="text-center">
                    <span className="text-4xl md:text-5xl font-serif text-foreground block mb-1">
                      {tier.price}
                    </span>
                    <span className="text-xs text-muted-foreground uppercase tracking-widest font-sans">
                      Program Investment
                    </span>
                  </div>
                  <Button asChild className={cn("w-full max-w-[160px] rounded-full transition-all duration-300 font-sans", tier.isFeatured ? "bg-accent text-accent-foreground hover:bg-accent/90" : "bg-secondary/20 text-foreground hover:bg-secondary/40 shadow-none")}>
                    <Link to="/ContactPage">Apply Now</Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}